import { createContext, useContext } from "react";
import * as React from "react";
import axios from "axios";

interface StationLocationContextType {
  stationLocation: BranchView;
}

const StationLocationContext = createContext<StationLocationContextType>(null);

export const useStationLocation = () => {
  const context = useContext(StationLocationContext);

  if (!context) {
    throw new Error(
      "useStationLocation must be used within a StationLocationProvider"
    );
  }

  return context;
};

interface BranchView {
  id: number;
  displayName: string;
  longDescription: string;
  identifier: string;
  isShared: boolean;
}

export const StationLocationHolder = (() => {
  let stationLocation: BranchView | null = null;
  const subscribers: ((event: BranchView) => void)[] = []; // Array to hold subscriber functions

  const subscribe = (subscriber: (stationLocation: BranchView) => void) => {
    // Add the subscriber function to the list of subscribers
    subscribers.push(subscriber);

    fetchStationLocation();

    if (stationLocation) {
      subscriber(stationLocation);
    }

    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }

      // if everyone has unsubscribed, clear the held data
      // this helps with unit tests
      if (subscribers.length === 0) {
        stationLocation = null;
      }
    };
  };

  const fetchStationLocation = () => {
    axios
      .get<BranchView>("/stationLocation/thisBranch")
      .then(({ data }) => {
  
        // response can come back as an empty string when the backend returns null
        // not 100% sure about when it returns a BranchView
        if(typeof data === "string"){
            try {
                stationLocation = JSON.parse(data);
            }
            catch(e){
                stationLocation = null;
            }
        }
        else {
            stationLocation = { ...data };
        }
        subscribers.forEach((subscriber) => subscriber(stationLocation));
      })
      .catch((error) => {
        console.error("Error getting Station Location", error);
        subscribers.forEach((subscriber) => subscriber(null));
      });
  };

  return {
    subscribe,
  };
})();

export const StationLocationProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [stationLocation, setStationLocation] =
    React.useState<BranchView>(null);

  // Fetch branch limits on load
  React.useEffect(() => {
    const unsubscribe = StationLocationHolder.subscribe((stationLocation) => {
      setStationLocation(stationLocation);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Define the context value
  const contextValue = {
    stationLocation,
  };

  return (
    <StationLocationContext.Provider value={contextValue}>
      {children}
    </StationLocationContext.Provider>
  );
};
